import { UseFormRegister, UseFormSetValue, FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { Check } from 'lucide-react';
import Input from '@components/atoms/ui-kit/input';
import { WizardFormData, PLAN_TYPE_OPTIONS } from './create-plan-wizard-types';

interface Props {
  watchedValues: WizardFormData;
  setValue: UseFormSetValue<WizardFormData>;
  register: UseFormRegister<WizardFormData>;
  errors: FieldErrors<WizardFormData>;
}

export function WizardStep1PlanInfo({
  watchedValues,
  setValue,
  register,
  errors
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const planType = watchedValues.type;

  return (
    <div className="space-y-6">
      {/* Plan Type Selection */}
      <div>
        <h4
          className="text-sm font-medium mb-3"
          style={{ color: getColour('normalText') }}
        >
          {t('plan-type')} <span className="text-red-500">*</span>
        </h4>
        <div className="grid grid-cols-3 gap-2">
          {PLAN_TYPE_OPTIONS.map(option => {
            const isSelected = planType === option.type;
            const Icon = option.icon;

            return (
              <button
                key={option.type}
                type="button"
                onClick={() => setValue('type', option.type)}
                className="flex items-center gap-2 px-3 py-2 rounded-2xl border-2 text-left transition-all"
                style={{
                  borderColor: isSelected ? option.color : getColour('border'),
                  backgroundColor: isSelected
                    ? option.bgColor
                    : getColour('secondLayer')
                }}
              >
                <div
                  className="h-6 w-6 rounded-2xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: option.bgColor }}
                >
                  <Icon
                    className="h-3.5 w-3.5"
                    style={{ color: option.color }}
                  />
                </div>
                <span
                  className="text-xs font-medium"
                  style={{ color: getColour('normalText') }}
                >
                  {t(option.type.toLowerCase().replace(/ /g, '-'))}
                </span>
                {isSelected && (
                  <Check
                    className="h-3 w-3 ml-auto shrink-0"
                    style={{ color: option.color }}
                  />
                )}
              </button>
            );
          })}
        </div>
        {errors.type && (
          <p className="text-xs text-red-500 mt-1">{errors.type.message}</p>
        )}
      </div>

      {/* SKU and Description */}
      <div
        className="border-t py-4"
        style={{ borderColor: getColour('border') }}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label={<>{t('sku')}</>}
            size="small"
            {...register('sku')}
            error={errors.sku?.message}
            required
            placeholder={t('enter-plan-sku')}
          />
          <Input
            label={t('description')}
            size="small"
            {...register('description')}
            placeholder={t('enter-plan-description')}
          />
        </div>
      </div>
    </div>
  );
}
